










































































































































import { AconexImportDialogParams } from '@/components/documents/aconex-import-dialog/aconex-import-dialog-models';
import {
  AddEditDialogMode,
  AddEditDocumentDialogParams,
} from '@/components/documents/add-edit-document-dialog/add-edit-document-dialog-models';
import { SelectDocumentDialogParams } from '@/components/documents/select-document-dialog/select-document-dialog-models';
import OtButton, { OtBtnSize, OtBtnStyle, OtBtnType } from '@/components/global/ot-button.vue';
import OtSelect, { ISelectItem } from '@/components/global/ot-select.vue';
import { doAconexImport } from '@/services/aconex.service';
import { vxm } from '@/store';
import { OtDataEntryRequirement } from '@/types/data-entry-requirement-enums';
import {
  OtSegmentInstanceDocumentVersionAssociationType,
  OtSegmentInstanceDocumentVersionAssociationTypeEnum,
  segmentInstanceDocumentVersionAssociationTypes,
} from '@/types/segment-instance-document-version-association-type';
import { convertAndFormatDateAndTime } from '@/utils/date-utils';
import { IVForm } from '@/utils/type-utils';
import { dirtyFormClass } from '@/utils/validation-utils';
import { uniqueId } from 'lodash';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import { SegmentInstanceDetails, SegmentInstanceDocument } from './claims-models';
@Component({
  components: {
    OtButton,
    OtSelect,
  },
})
export default class OtClaimDocumentsForm extends Vue {
  // * PROPS
  @Prop() private claimDetails?: SegmentInstanceDetails;
  @Prop() private claimDocuments?: SegmentInstanceDocument[];

  // * REFS
  @Ref('claimDocumentsFormRef') private readonly claimDocumentsFormRef!: IVForm;

  // * DATA
  private buttonSize = OtBtnSize.Tiny;
  private buttonStyle = OtBtnStyle.Outline;
  private buttonType = OtBtnType.Icon;

  private originalThumbprint = '';
  private currentThumbprint = '';
  // we don't want to try and mutate the prop, so we'll keep our own copy
  private documentsCopy: SegmentInstanceDocument[] = [];

  // * COMPUTED
  private get formIsDirty(): boolean {
    // the eagled eyed among us will notice that we're not actually setting the currentThumbprint anywhere
    // I don't think we need it. If they add a document then hit f5, they'll lose the change, boo hoo.
    return this.originalThumbprint !== this.currentThumbprint;
  }
  private get dirtyFormClass(): string {
    return dirtyFormClass;
  }
  private get displayDocuments() {
    // the original idea was to show the removed documents in a different colour
    // this never got designed nor implemented, and the client reckons they don't need it
    // so we'll just not show them
    return this.documentsCopy.filter(d => !d.isRemoved);
  }

  // * WATCHERS
  @Watch('claimDocuments')
  private onClaimDocumentsChange(newVal: SegmentInstanceDocument[]) {
    console.log('ot-claim-documents-form -> onClaimDocumentsChange -> newVal', [...newVal]);
    // we could try and merge the new val into the current val, but nah
    this.documentsCopy = [...newVal];
  }

  private showOriginatingDocumentMandatoryMessage = false;

  // * METHODS
  private convertAndFormatDateAndTime(date: Date | null, timeZone: string | null) {
    if (date && timeZone) {
      return convertAndFormatDateAndTime(date, timeZone, { overrideJoin: '-' });
    }
    return '';
  }

  public validate() {
    const formIsValid = this.claimDocumentsFormRef.validate();

    const hasAtLeastOneOriginatingDocument = this.documentsCopy.some(
      d => d.associationType?.value === OtSegmentInstanceDocumentVersionAssociationTypeEnum.Originating && !d.isRemoved,
    );

    const hasMoreThanOneOriginatingDocument =
      this.documentsCopy.filter(
        d =>
          d.associationType?.value === OtSegmentInstanceDocumentVersionAssociationTypeEnum.Originating && !d.isRemoved,
      ).length > 1;

    const needsOriginatingDocument =
      this.claimDetails?.workflowSegment.config?.documentRequiredOnCreation === OtDataEntryRequirement.Mandatory;

    // I don't know if this is the right check or not. If Mandatory is yes, then forbidden could be no?
    // it's not in the spec, so I'm leaving it out
    // const isNotAllowedAnyOrignatingDocuments =
    //   this.claimDetails?.workflowSegment.config?.documentRequiredOnCreation === OtDataEntryRequirement.Forbidden;

    for (const document of this.documentsCopy) {
      document.showMultipleOriginatingDocumentsMessage =
        hasMoreThanOneOriginatingDocument &&
        document.associationType?.value === OtSegmentInstanceDocumentVersionAssociationTypeEnum.Originating &&
        !document.isRemoved;
    }

    const noDocumentsHaveErrors = this.documentsCopy.every(d => d.associationTypeErrorMessages.length === 0);

    this.showOriginatingDocumentMandatoryMessage = needsOriginatingDocument && !hasAtLeastOneOriginatingDocument;
    // this.showOriginatingDocumentForbiddenMessage =
    //   isNotAllowedAnyOrignatingDocuments && hasAtLeastOneOriginatingDocument;

    return formIsValid && noDocumentsHaveErrors && !this.showOriginatingDocumentMandatoryMessage;
  }
  public setFormToCleanState() {
    // Normally this would be thumprint stuff for "you've got unsaved changes" detection.
    // It'd stop users from adding/removing a document and forgetting to click Next I guess?
    // But that's highly unlikely to happen. So, meh, we're not bothering here
    this.originalThumbprint = this.currentThumbprint;
  }

  // we're looking to build a SegmentInstanceDocumentsPost object. Which is used to make a PostUpdateSegmentInstanceDocumentsRequestModel
  public async submit(validate = true): Promise<SegmentInstanceDocument[] | null> {
    if (validate && !this.validate()) {
      return Promise.resolve(null);
    }
    // uh... ?
    // we should be setting the form to a clean state here, but the dirty is tracked inside the objects themselves
    // and it's important for building the api request model
    // so, I don't know. Do we actually need to reset? I don't think so. As long as the API sends us a new object with
    // the original values loaded, we should be good.
    return Promise.resolve(this.documentsCopy);
  }

  // eslint-disable-next-line prettier/prettier
  private associationTypeItems: ISelectItem<OtSegmentInstanceDocumentVersionAssociationType>[] =
    segmentInstanceDocumentVersionAssociationTypes.map(t => ({ label: t.name, data: t }));

  private async createDocument() {
    if (!this.claimDetails?.project.organisationGid) {
      console.warn('ot-claim-documents-form -> createDocument -> no organisationGid, bailing');
      return;
    }
    const params = new AddEditDocumentDialogParams({
      context: 'Workflow',
      targetOrganisationGid: this.claimDetails.project.organisationGid,
      mode: AddEditDialogMode.CreateForWorkflow,
    });
    const dialogResult = await vxm.modal.openAddEditDocumentDialog(params);
    if (dialogResult.ok && dialogResult.document) {
      this.documentsCopy.push(
        new SegmentInstanceDocument({
          key: uniqueId(),
          associationType: null, // deliberately start off with no association type, the user MUST set this. This was important to the client
          contentDescription: dialogResult.document.contentDescription,
          receivalType: dialogResult.document.receivalType,
          receivalDescription: dialogResult.document.receivalDescription,
          deemedReceivedTimeZone: dialogResult.document.deemedReceivedTimeZone,
          deemedReceivedUtc: dialogResult.document.deemedReceivedUtc,
          documentVersionGid: dialogResult.document.documentVersionGid,
          files: [],
          gid: dialogResult.document.gid,
          isLocked: false,
          isNew: true,
          isRemoved: false,
          name: dialogResult.document.name,
          reference: dialogResult.document.reference,
          segmentInstanceDocumentVersionGid: '', // definitely do not have this yet
          documentVersionIndex: 1, // if we're creating it, it's version 1
          documentVersionIsLatestVersion: true, // if we're creating it, it's the latest version
        }),
      );
    }
  }
  private async editDocument(document: SegmentInstanceDocument) {
    if (!this.claimDetails?.project.organisationGid) {
      console.warn('ot-claim-documents-form -> editDocument -> no organisationGid, bailing');
      return;
    }
    if (!document.gid) {
      console.warn('ot-claim-documents-form -> editDocument -> document has no gid, bailing');
      return;
    }
    const params = new AddEditDocumentDialogParams({
      context: 'Workflow',
      targetOrganisationGid: this.claimDetails.project.organisationGid,
      mode: AddEditDialogMode.Edit,
      documentGid: document.gid,
    });
    // You might be wondering what to do if version 5 is attached to the claim. Then they make a version 6, external to the claim
    // then they come back and edit from within the claim. We have no choice but to edit the MOST RECENT version.
    // So you'll see no reference to the version here
    // Will it confuse the users? Definite maybe. If v5 had the title "DOCDOC" but v6 has the title "BOKBOK"
    // the user will probably be confused what is going on.
    // We could put a modal in saying "v5 was attached, the latest is v6, the edit might be different to what you're seeing"
    // But, later, not now.
    const dialogResult = await vxm.modal.openAddEditDocumentDialog(params);
    if (dialogResult.ok && dialogResult.document) {
      // whatever happens, we need to add a new document into the array
      const newDocument = new SegmentInstanceDocument({
        key: uniqueId(),
        associationType: document.associationType, // reuse the old association type
        contentDescription: dialogResult.document.contentDescription,
        receivalType: dialogResult.document.receivalType,
        receivalDescription: dialogResult.document.receivalDescription,
        deemedReceivedTimeZone: dialogResult.document.deemedReceivedTimeZone,
        deemedReceivedUtc: dialogResult.document.deemedReceivedUtc,
        documentVersionGid: dialogResult.document.documentVersionGid,
        files: [],
        gid: dialogResult.document.gid,
        isLocked: false,
        isNew: true,
        isRemoved: false,
        name: dialogResult.document.name,
        reference: dialogResult.document.reference,
        segmentInstanceDocumentVersionGid: '', // definitely do not have this yet
        // If they've added V3, then external to the workflow edited once to make a V4,
        // then they come back to the workflow and edit it here, we'll claim that it's V4. It's not, it's actually V5.
        // Short of make an API call here to retrieve the version, or add the version number to the return from the edit (which would be an idea)
        // this is the best we can do. If, at the time we loaded the form, it was the most recent version, then pretend like we
        // made the version + 1. It might still be a lie if they're particularly determined annoying users with multiple tabs open
        documentVersionIndex: document.documentVersionIsLatestVersion ? document.documentVersionIndex + 1 : 0,
        documentVersionIsLatestVersion: true, // if we've just edited it, then we will have made the latest version
      });
      const existingDocumentIndex = this.documentsCopy.indexOf(document);
      if (existingDocumentIndex === -1) {
        // this is bad. We are editing a document but we can't find it in our array. The universe is broken. Again.
        console.error('ot-claim-documents-form -> editDocument -> existing document not found in array, bailing');
        return;
      }
      if (document.isNew) {
        // if the old document was new, just replace it with the new one. It's never been to the server, so we don't need to remove it on the server
        // We could, if we were feeling feisty, keep the old one and visualise "new but removed" differently, but meh
        this.documentsCopy.splice(existingDocumentIndex, 1, newDocument);
      } else {
        // if the old document was not new, it's been to the server, so we need to mark it as removed so it gets removed on the server
        document.isRemoved = true;
        // and then add the new one. May as well splice it in next to the old one
        this.documentsCopy.splice(existingDocumentIndex, 0, newDocument);
      }
    }
  }

  private removeDocument(document: SegmentInstanceDocument) {
    if (document.isLocked) {
      console.error('ot-claim-documents-form -> removeDocument -> document is locked, bailing');
      return;
    }
    if (document.isNew) {
      // if it's new, we can just remove it from the array, it's never been to the server so we don't need to remove it on the server
      const existingDocumentIndex = this.documentsCopy.indexOf(document);
      if (existingDocumentIndex === -1) {
        console.error('ot-claim-documents-form -> removeDocument -> document not found in array, bailing');
        return;
      }
      this.documentsCopy.splice(existingDocumentIndex, 1);
    } else {
      // if it's not new, we need to mark it as removed so it gets removed on the server
      document.isRemoved = true;
    }
  }

  private async selectDocument() {
    if (!this.claimDetails?.project.organisationGid) {
      console.warn('ot-claim-documents-form -> selectDocument -> no organisationGid, bailing');
      return;
    }
    const params = new SelectDocumentDialogParams({
      targetOrganisationGid: this.claimDetails.project.organisationGid,
      context: 'Workflow',
    });
    const dialogResult = await vxm.modal.openSelectDocumentDialog(params);
    if (dialogResult.ok) {
      for (const document of dialogResult.selectedDocuments) {
        // There's a chance they're playing silly buggers and selecting a document that's already in the claim
        // that they removed, then selected again. Why? Who knows. Mistake is the most charitable answer.
        // There's also a chance they're selecting a document that's already in the claim, but not removed.
        // Why? Who knows. Mistake is the most charitable answer.
        // We can't have the same version in there twice
        // I'd like to yell at them for selecting something that's already in there, but it's not spec'd
        // So we'll just make sure that whatever they have is not removed
        // and do nothing if they're adding D1 when D1 is already in there
        // "But Dan, what about v1 and v2 of the same document?"
        // Shush penfold. That's not spec'd either. The server won't barf, so we'll let it through.
        const existingDocument = this.documentsCopy.find(d => d.documentVersionGid === document.documentVersionGid);
        if (existingDocument) {
          existingDocument.isRemoved = false;
        } else {
          this.documentsCopy.push(
            new SegmentInstanceDocument({
              associationType: null, // deliberately start off with no association type, the user MUST set this. This was important to the client
              contentDescription: document.contentDescription,
              receivalType: document.receivalType,
              receivalDescription: document.receivalDescription,
              deemedReceivedTimeZone: document.deemedReceivedTimeZone,
              deemedReceivedUtc: document.deemedReceivedUtc,
              documentVersionGid: document.documentVersionGid,
              files: [], // don't care about files right now
              gid: document.gid,
              isLocked: false,
              isNew: true,
              isRemoved: false,
              key: uniqueId(),
              name: document.name,
              reference: document.reference,
              segmentInstanceDocumentVersionGid: '', // definitely do not have this yet
              documentVersionIndex: document.versionIndex,
              documentVersionIsLatestVersion: true, // if they've selected it, it's the latest version
            }),
          );
        }
      }
    }
  }
  private async importFromAconex() {
    if (!this.claimDetails?.project.organisationGid) {
      console.warn('ot-claim-documents-form -> importFromAconex -> no organisationGid, bailing');
      return;
    }
    const documentParams = new AddEditDocumentDialogParams({
      targetOrganisationGid: this.claimDetails.project.organisationGid,
      context: 'Workflow',
      mode: AddEditDialogMode.CreateForWorkflow,
    });

    const result = await doAconexImport(new AconexImportDialogParams({ documentParams }));
    if (result.didImport) {
      this.documentsCopy.push(
        new SegmentInstanceDocument({
          associationType: null, // deliberately start off with no association type, the user MUST set this. This was important to the client
          contentDescription: result.result?.document?.contentDescription ?? null,
          receivalType: result.result?.document?.receivalType ?? null,
          receivalDescription: result.result?.document?.receivalDescription ?? null,
          deemedReceivedTimeZone: result.result?.document?.deemedReceivedTimeZone ?? null,
          deemedReceivedUtc: result.result?.document?.deemedReceivedUtc ?? null,
          documentVersionGid: result.result?.document?.documentVersionGid ?? '',
          files: [], // don't care about files right now
          gid: result.result?.document?.gid ?? '',
          isLocked: false,
          isNew: true,
          isRemoved: false,
          key: uniqueId(),
          name: result.result?.document?.name ?? '',
          reference: result.result?.document?.reference ?? '',
          segmentInstanceDocumentVersionGid: '', // definitely do not have this yet
          documentVersionIndex: 1, // if they've just imported it, it is version 1
          documentVersionIsLatestVersion: true, // if they've just imported it, it is version 1
        }),
      );
    }
  }
  private getAssociationTypeOption(document: SegmentInstanceDocument) {
    return this.associationTypeItems.find(i => i.data?.value === document.associationType?.value);
  }

  private setAssociationTypeOption(
    document: SegmentInstanceDocument,
    option: ISelectItem<OtSegmentInstanceDocumentVersionAssociationType>,
  ) {
    document.associationType = option.data;
  }

  private mounted() {
    console.log('ot-claim-documents-form -> mounted -> this.claimDocuments', [...(this.claimDocuments ?? [])]);
    this.documentsCopy = [...(this.claimDocuments ?? [])];
  }

  // * LIFECYCLE
}
